import React, { useState } from "react";
import { BotMessageSquare, X, Send, Zap } from "lucide-react";
import "./AiAssistantPopup.scss";

const SUGGESTED_PROMPTS = [
    "Summarize the page",
    "Explain quality scores",
    "What filters are active?",
];

export default function AiAssistantPopup() {
    const [open, setOpen] = useState(false);
    const [input, setInput] = useState("");
    const [comingSoon, setComingSoon] = useState(false);

    const handlePromptClick = () => {
        setComingSoon(true);
        setTimeout(() => setComingSoon(false), 2500);
    };

    const handleClose = () => {
        setOpen(false);
        setComingSoon(false);
        setInput("");
    };

    return (
        <>
            <div
                className="fixed bottom-2 right-8 z-[1300] w-[88px] h-[88px] flex items-center justify-center cursor-pointer"
                onClick={() => setOpen(true)}
            >
                <div className="bot-ring-md absolute inset-[10px] rounded-full border-[1.5px] border-[rgba(217,207,255,0.55)] border-b-[rgba(217,207,255,0.15)] border-l-[rgba(217,207,255,0.15)]" />
                <div className="bot-ring-sm absolute inset-[18px] rounded-full border-[1.5px] border-[rgba(200,190,255,0.6)] border-t-[rgba(200,190,255,0.15)] border-l-[rgba(200,190,255,0.15)]" />
                <div className="bot-circle absolute inset-[22px] rounded-full bg-gradient-to-br from-[#7b6fe8] via-[#5b4fd8] to-[#4535c8] flex items-center justify-center">
                    <div className="bot-icon-rotate flex items-center justify-center">
                        <BotMessageSquare size={22} color="white" />
                    </div>
                </div>
            </div>

            {/* ── Popup ── */}
            {open && (
                <div
                    className="fixed inset-0 z-[1400]"
                    onClick={handleClose}
                >
                    <div
                        className="ai-popup fixed flex flex-col overflow-hidden bottom-[15px] right-[25px] w-[420px] h-[min(600px, calc(100vh - 120px))] rounded-2xl bg-white shadow-[0_20px_60px_rgba(80,60,200,0.25)]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="bg-gradient-to-r from-[#5b4fd8] to-[#4535c8] px-4 py-[14px] flex items-center justify-between shrink-0">
                            <div className="flex items-center gap-[10px]">
                                <div className="w-[38px] h-[38px] rounded-full bg-white/20 flex items-center justify-center">
                                    <BotMessageSquare size={20} color="white" />
                                </div>
                                <div>
                                    <div className="flex items-center gap-1 text-[15px] font-semibold text-white">
                                        AI Assistant
                                        <Zap size={16} fill="#f59e0b" color="#f59e0b" /> (Beta Version)
                                    </div>
                                    <div className="text-[11px] text-white/75 mt-[1px]">
                                        Ask me anything about your data
                                    </div>
                                </div>
                            </div>
                            <button
                                className="bg-transparent border-none cursor-pointer p-1 flex items-center opacity-80 hover:opacity-100"
                                onClick={handleClose}
                            >
                                <X size={18} color="white" />
                            </button>
                        </div>

                        {/* Body */}
                        <div className="flex-1 overflow-y-auto px-5 pt-6 pb-4 relative bg-white min-h-0">

                            {/* Welcome */}
                            <div className="flex flex-col items-center text-center mb-7">
                                <div className="relative mb-4">
                                    <Zap
                                        size={18}
                                        className="ai-zap-icon absolute -top-1 -left-3"
                                        fill="#e040fb"
                                        color="#e040fb"
                                    />
                                    <div className="w-[72px] h-[72px] rounded-full bg-[#ede9ff] flex items-center justify-center">
                                        <div className="ai-welcome-bot-spin flex">
                                            <BotMessageSquare size={26} color="#5b4fd8" />
                                        </div>
                                    </div>
                                </div>
                                <h3 className="text-[17px] font-bold text-[#1a1a2e] m-0 mb-[6px]">
                                    Welcome to Data Pulse Assistant
                                </h3>
                                <p className="text-[13px] text-gray-500 m-0">
                                    I can help you understand and analyze your asset data
                                </p>
                            </div>

                            {/* Coming Soon Overlay */}
                            {comingSoon && (
                                <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-10 rounded-b-2xl">
                                    <div className="ai-popup flex items-center gap-[6px] bg-gradient-to-r from-[#5b4fd8] to-[#7b6fe8] text-white px-[22px] py-[10px] rounded-full text-[14px] font-semibold shadow-[0_6px_20px_rgba(90,70,220,0.4)]">
                                        <Zap size={16} fill="#f59e0b" color="#f59e0b" />
                                        Coming Soon
                                    </div>
                                </div>
                            )}

                            {/* Suggested Prompts */}
                            <div className={`transition-opacity duration-300 ${comingSoon ? "opacity-40 pointer-events-none" : ""}`}>
                                <p className="text-[10px] font-bold tracking-[0.08em] text-gray-400 m-0 mb-[10px]">
                                    SUGGESTED PROMPTS
                                </p>
                                {SUGGESTED_PROMPTS.map((prompt) => (
                                    <button
                                        key={prompt}
                                        className="w-full text-left bg-white border border-gray-200 rounded-[10px] px-4 py-[13px] text-[14px] text-[#1a1a2e] cursor-pointer mb-2 transition-all duration-200 hover:border-purple-400 hover:bg-purple-50 disabled:cursor-not-allowed disabled:opacity-45"
                                        onClick={handlePromptClick}
                                        disabled={comingSoon}
                                    >
                                        {prompt}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Footer */}
                        <div className={`px-4 py-3 border-t border-gray-100 flex gap-[10px] items-center bg-white shrink-0 transition-opacity duration-300 ${comingSoon ? "opacity-40 pointer-events-none" : ""}`}>
                            <input
                                className="flex-1 border border-gray-200 disabled rounded-full px-4 py-[10px] text-[13px] outline-none text-[#1a1a2e] placeholder-[#b0b0c0] focus:border-purple-400 disabled:bg-gray-50 disabled:cursor-not-allowed"
                                placeholder="Coming Soon"
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                readOnly
                            />
                            <button
                                className="w-[38px] h-[38px] rounded-full bg-gradient-to-br from-[#5b4fd8] to-[#7b6fe8] border-none cursor-pointer flex items-center justify-center flex-shrink-0 transition-opacity duration-200 hover:opacity-90 disabled:opacity-45 disabled:cursor-not-allowed"
                                disabled={comingSoon || !input.trim()}
                            >
                                <Send size={16} color="white" className="cursor-not-allowed" />
                            </button>
                        </div>

                    </div>
                </div>
            )}
        </>
    );
}